import React, { useEffect, useState } from "react";
import useGameState from "../hooks/useGameState";
import Header from "../components/Header";
import PlayerProfile from "../components/PlayerProfile";
import QuestList from "../components/QuestList";
import WeeklyCalendar from "../components/WeeklyCalendar";
import RankLadder from "../components/RankLadder";
import LevelUpModal from "../components/LevelUpModal";
import AvatarPicker from "../components/AvatarPicker";
import UsernameDialog from "../components/UsernameDialog";
import Onboarding from "../components/Onboarding";
import HistoryLog from "../components/HistoryLog";
import Achievements from "../components/Achievements";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Toaster, toast } from "sonner";
import { sfx, primeAudio, setMuted, isMuted } from "../lib/sounds";

export default function Dashboard() {
  const {
    state,
    lastEvent,
    clearLastEvent,
    achievementToast,
    clearAchievementToast,
    setUsername,
    setAvatar,
    completeOnboarding,
    toggleQuest,
    addQuest,
    editQuest,
    removeQuest,
    completeDay,
    resetAll,
  } = useGameState();

  const [levelEvent, setLevelEvent] = useState(null);
  const [pendingRankEvent, setPendingRankEvent] = useState(null);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [nameOpen, setNameOpen] = useState(false);
  const [muted, setMutedState] = useState(isMuted());

  // React to a fresh complete-day event published by the hook.
  useEffect(() => {
    if (!lastEvent) return;
    if (lastEvent.leveledUp) {
      if (lastEvent.rankedUp) {
        setPendingRankEvent({ ...lastEvent, rankedUp: true, leveledUp: false });
        setLevelEvent({ ...lastEvent, rankedUp: false });
      } else {
        setLevelEvent({ ...lastEvent, rankedUp: false });
      }
      setTimeout(() => sfx.levelUp(), 150);
    }
    clearLastEvent();
  }, [lastEvent, clearLastEvent]);

  // Prime audio context on first user interaction
  useEffect(() => {
    const handler = () => primeAudio();
    window.addEventListener("pointerdown", handler, { once: true });
    return () => window.removeEventListener("pointerdown", handler);
  }, []);

  // Achievement unlock toasts
  useEffect(() => {
    if (!achievementToast || achievementToast.length === 0) return;
    achievementToast.forEach((a, i) => {
      setTimeout(() => {
        toast(`Title Unlocked: ${a.title}`, {
          description: a.desc,
          className: "font-mono",
        });
        sfx.questComplete();
      }, i * 350);
    });
    clearAchievementToast();
  }, [achievementToast, clearAchievementToast]);

  if (!state.onboarded) {
    return (
      <>
        <Toaster theme="dark" position="top-center" />
        <Onboarding
          onComplete={(name, avatarId) => {
            completeOnboarding(name, avatarId);
            toast.success(`Welcome to The System, ${name}.`, {
              className: "font-mono",
            });
          }}
        />
      </>
    );
  }

  const handleCompleteDay = () => {
    const res = completeDay();
    if (!res) return;
    toast.success(
      `Day cleared. +${res.gain} XP · ${res.questsCompleted} quests`,
      { className: "font-mono" },
    );
    // Level/rank event handling is performed via the lastEvent useEffect above.
  };

  const handleLevelClose = () => {
    setLevelEvent(null);
    if (pendingRankEvent) {
      // small delay so the next modal feels like a sequence
      setTimeout(() => {
        sfx.rankUp();
        setLevelEvent({ ...pendingRankEvent, rankedUp: true });
        setPendingRankEvent(null);
      }, 250);
    }
  };

  return (
    <div className="min-h-screen">
      <Toaster
        theme="dark"
        position="top-center"
        toastOptions={{
          className:
            "bg-[#0A0D14] !border !border-purple-500/30 !rounded-none text-white font-mono",
        }}
      />
      <Header
        onReset={() => {
          resetAll();
          toast("System purged.");
        }}
        muted={muted}
        onToggleMute={() => {
          const next = !muted;
          setMuted(next);
          setMutedState(next);
          if (!next) sfx.click();
        }}
      />

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8 space-y-6">
        <PlayerProfile
          state={state}
          onEditAvatar={() => setAvatarOpen(true)}
          onEditName={() => setNameOpen(true)}
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Tabs defaultValue="quests" className="w-full">
              <TabsList
                className="bg-[#0A0D14] border border-white/10 rounded-none p-1 h-auto gap-1"
                data-testid="main-tabs"
              >
                <TabsTrigger
                  value="quests"
                  className="rounded-none data-[state=active]:bg-purple-600 data-[state=active]:text-white text-slate-400 font-display uppercase tracking-widest text-xs px-4 py-2"
                  data-testid="tab-quests"
                >
                  Quests
                </TabsTrigger>
                <TabsTrigger
                  value="history"
                  className="rounded-none data-[state=active]:bg-purple-600 data-[state=active]:text-white text-slate-400 font-display uppercase tracking-widest text-xs px-4 py-2"
                  data-testid="tab-history"
                >
                  History
                </TabsTrigger>
                <TabsTrigger
                  value="achievements"
                  className="rounded-none data-[state=active]:bg-purple-600 data-[state=active]:text-white text-slate-400 font-display uppercase tracking-widest text-xs px-4 py-2"
                  data-testid="tab-achievements"
                >
                  Titles
                </TabsTrigger>
              </TabsList>
              <TabsContent value="quests" className="mt-4">
                <QuestList
                  quests={state.quests}
                  onToggle={toggleQuest}
                  onAdd={addQuest}
                  onEdit={editQuest}
                  onRemove={removeQuest}
                  onCompleteDay={handleCompleteDay}
                />
              </TabsContent>
              <TabsContent value="history" className="mt-4">
                <div className="border border-white/10 bg-[#0A0D14] p-5 md:p-6">
                  <HistoryLog history={state.history || []} />
                </div>
              </TabsContent>
              <TabsContent value="achievements" className="mt-4">
                <div className="border border-white/10 bg-[#0A0D14] p-5 md:p-6">
                  <Achievements unlocked={state.achievements || {}} />
                </div>
              </TabsContent>
            </Tabs>
            <WeeklyCalendar state={state} />
          </div>
          <div className="space-y-6">
            <RankLadder level={state.level} />
            <SystemTip />
          </div>
        </div>

        <footer className="text-center text-[10px] uppercase tracking-[0.3em] text-slate-600 pt-4 pb-8">
          // Arise · Endure · Ascend
        </footer>
      </main>

      <AvatarPicker
        open={avatarOpen}
        onOpenChange={setAvatarOpen}
        current={state.avatarId}
        onSelect={(id) => {
          setAvatar(id);
          toast(`Vessel switched.`);
        }}
      />
      <UsernameDialog
        open={nameOpen}
        onOpenChange={setNameOpen}
        current={state.username}
        onSave={(n) => {
          setUsername(n);
          toast(`Codename updated.`);
        }}
      />
      <LevelUpModal event={levelEvent} onClose={handleLevelClose} />
    </div>
  );
}

const SystemTip = () => (
  <div className="border border-blue-500/20 bg-blue-500/5 p-5 relative overflow-hidden">
    <div className="absolute inset-y-0 left-0 w-px bg-gradient-to-b from-transparent via-blue-400 to-transparent" />
    <div className="text-[10px] uppercase tracking-[0.3em] text-blue-300">
      // System Notice
    </div>
    <p className="mt-2 text-sm text-slate-300 leading-relaxed">
      Complete at least one quest each day to maintain your streak. The penalty
      for inactivity is the dissolution of your aura — your streak resets to
      zero.
    </p>
  </div>
);
